import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import VotingCard from "../components/VotingCard";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE } from "../utils/Instance";
import { toast } from "sonner";

export default function VotingPanel() {
  const { profile, refreshAccessToken } = useAuth();
  const navigate = useNavigate();
  const role = profile?.role || "";
  const params = useParams();
  const contestId = params.id;
  const [contestStartTime, setContestStartTime] = useState(null);
  const [contestEndTime, setContestEndTime] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [checkingStartTime, setCheckingStartTime] = useState(true);
  
  const [currentMatch, setCurrentMatch] = useState([null, null]); // 1v1 Match: [Participant 1, Participant 2]
  const [currentMatchupId, setCurrentMatchupId] = useState(null);
  const [waitingQueue, setWaitingQueue] = useState([]); // Participants waiting for their turn
  const [loading, setLoading] = useState(true);
  const [winner, setWinner] = useState(null);
  const [votes, setVotes] = useState({ left: 0, right: 0 });
  const [roundActive, setRoundActive] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [hasVoted, setHasVoted] = useState(false); // Track if user has voted in current round
  const [matchupsCompleted, setMatchupsCompleted] = useState(false);
  const [matchupProgress, setMatchupProgress] = useState(null);
  
  const isFetchingParticipants = useRef(false);
  const matchRef = useRef([null, null]);
  const matchupIdRef = useRef(null);
  const isTransitioning = useRef(false);
  const voteInFlightRef = useRef(false);
  const advanceTimerRef = useRef(null);

  // Sync refs with state for use in callbacks
  useEffect(() => { matchRef.current = currentMatch; }, [currentMatch]);
  useEffect(() => { matchupIdRef.current = currentMatchupId; }, [currentMatchupId]);

  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
      }
    };
  }, []);

  const resetRoundState = ({ activate = false } = {}) => {
    setVotes({ left: 0, right: 0 });
    setRoundActive(activate);
    setShowResults(false);
    setWinner(null);
    setHasVoted(false);
    voteInFlightRef.current = false;
  };

  const applyServerMatchup = (matchup) => {
    const nextMatch = matchup?.participants || [];
    if (nextMatch.length !== 2) return false;

    setMatchupsCompleted(false);
    setCurrentMatch(nextMatch);
    setCurrentMatchupId(matchup.id);
    matchRef.current = nextMatch;
    matchupIdRef.current = matchup.id;
    setWaitingQueue([]);
    resetRoundState({ activate: true });
    console.log("Match set from server:", nextMatch.map(p => p.contributor_name));
    return true;
  };

  const preserveWinnerSlot = (matchup, selectedWinner, winnerIndex) => {
    const nextMatch = matchup?.participants || [];
    if (nextMatch.length !== 2 || !selectedWinner) return matchup;

    const currentWinnerIndex = nextMatch.findIndex((participant) => participant.id === selectedWinner.id);
    if (currentWinnerIndex === -1 || currentWinnerIndex === winnerIndex) return matchup;

    const reorderedMatch = [...nextMatch];
    [reorderedMatch[winnerIndex], reorderedMatch[currentWinnerIndex]] = [
      reorderedMatch[currentWinnerIndex],
      reorderedMatch[winnerIndex],
    ];

    return {
      ...matchup,
      participants: reorderedMatch,
    };
  };

  const readApiError = async (response, fallbackMessage) => {
    let errorData = null;
    try {
      errorData = await response.json();
    } catch (e) {
      // ignore JSON parse errors
    }

    const message = errorData?.error || errorData?.detail;
    return typeof message === "string"
      ? message
      : `${fallbackMessage} (${response.status})`;
  };

  const authenticatedFetch = async (url, options = {}) => {
    const token = profile?.accessToken;
    if (!token) {
      navigate("/login");
      return null;
    }

    const buildOptions = (authToken) => ({
      ...options,
      credentials: "include",
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${authToken}`,
      },
    });

    let response = await fetch(url, buildOptions(token));

    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        navigate("/login");
        return null;
      }
      response = await fetch(url, buildOptions(newToken));
    }

    return response;
  };

  // Fetch/resume the next server-side matchup for this user.
  const fetchParticipants = async () => {
    if (isFetchingParticipants.current) return;
    
    try {
      isFetchingParticipants.current = true;
      console.log("Fetching next matchup from API...");
      const response = await authenticatedFetch(`${API_BASE}accounts/votes/next_matchup/?contest_id=${contestId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response) return;
      
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to fetch matchup'));
      }
      
      const data = await response.json();
      setMatchupProgress(data.progress || null);
      
      if (data.completed) {
        setMatchupsCompleted(true);
        setCurrentMatch([null, null]);
        setCurrentMatchupId(null);
        setWaitingQueue([]);
        toast.success("All matchups completed! Loading results...");
        fetchFinalResults();
        return;
      }

      const matchup = data.matchup;
      if (!applyServerMatchup(matchup)) {
        setCurrentMatch([null, null]);
        setCurrentMatchupId(null);
        matchRef.current = [null, null];
        matchupIdRef.current = null;
        resetRoundState();
        toast.info(data.message || "Need at least 2 participants to start voting");
      }
    } catch (error) {
      console.error("Error fetching matchup:", error);
      toast.error(error.message || "Failed to load matchup");
    } finally {
      setLoading(false);
      isFetchingParticipants.current = false;
    }
  };

  // Fetch final contest results
  async function fetchFinalResults() {
    try {
      // Check if contest has actually ended before fetching results
      // to avoid 400 errors in console.
      const now = new Date();
      const endTime = contestEndTime ? new Date(contestEndTime) : null;
      
      if (endTime && now < endTime) {
        console.log("Contest has not ended yet, skipping final results fetch.");
        toast.info("🎉 Voting complete! Results will be available once the contest ends.");
        return;
      }

      const response = await authenticatedFetch(`${API_BASE}accounts/votes/contest_results/?contest_id=${contestId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response) return;

      if (!response.ok) {
        const message = await readApiError(response, 'Failed to fetch final results');

        // If contest has not ended yet, show info instead of generic error
        if (message.toLowerCase().includes('not ended')) {
          toast.info(message);
        } else {
          toast.error(message);
        }
        return;
      }

      const data = await response.json();
      setFinalResults(data);
      setShowFinalResults(true);
    } catch (error) {
      console.error('Error fetching final results:', error);
      toast.error('Failed to load results');
    }
  }
  
  // Start a new round
  const startNewRound = () => {
    resetRoundState({ activate: true });
  };
  
  // Handle vote (only for users, not contributors)
  const handleVote = (side) => {
    // Only users can vote
    if (role !== "user") {
      toast.error("Only users can vote!");
      return;
    }
    
    if (!roundActive || isTransitioning.current || voteInFlightRef.current) {
      toast.info("Round not active");
      return;
    }
    
    // Check if user has already voted in this round
    if (hasVoted) {
      toast.warning("You have already voted in this round!");
      return;
    }
    
    // Increment vote count
    const newVotes = {
      ...votes,
      [side]: votes[side] + 1
    };
    setVotes(newVotes);
    
    // Mark user as having voted
    setHasVoted(true);
    voteInFlightRef.current = true;
    
    // Immediately end round after user votes
    setTimeout(() => {
      endRound(newVotes);
    }, 100);
  };
  
  // End round and determine winner
  const endRound = async (votesToUse = votes) => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;

    setRoundActive(false);
    setShowResults(true);
    
    const [leftParticipant, rightParticipant] = matchRef.current;
    if (!leftParticipant || !rightParticipant) {
      voteInFlightRef.current = false;
      isTransitioning.current = false;
      return;
    }
    
    // Determine winner and loser based on votes
    const winnerIndex = votesToUse.left >= votesToUse.right ? 0 : 1;
    const selectedWinner = matchRef.current[winnerIndex];
    
    if (!selectedWinner) {
      voteInFlightRef.current = false;
      isTransitioning.current = false;
      return;
    }
    
    setWinner(selectedWinner);
    console.log(`Round ended. Winner: ${selectedWinner.contributor_name} (${selectedWinner.id}) in slot ${winnerIndex === 0 ? 'A' : 'B'}`);
    
    const activeMatchupId = matchupIdRef.current;
    if (!activeMatchupId) {
      toast.error("Matchup is missing. Please refresh and try again.");
      voteInFlightRef.current = false;
      isTransitioning.current = false;
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_BASE}accounts/votes/record_matchup_vote/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          matchup_id: activeMatchupId,
          winner_id: selectedWinner.id,
        })
      });

      if (!response) {
        voteInFlightRef.current = false;
        isTransitioning.current = false;
        return;
      }
      
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to record match result'));
      }
      
      const data = await response.json();
      toast.success("Vote recorded! 👍");
      setMatchupProgress(data.progress || null);
      
      // Let the user see the winner briefly, then replace the full matchup
      // in one state reset so the UI cannot stay pinned to the previous pair.
      advanceTimerRef.current = setTimeout(() => {
        if (data.completed) {
          setMatchupsCompleted(true);
          setCurrentMatch([null, null]);
          setCurrentMatchupId(null);
          matchRef.current = [null, null];
          matchupIdRef.current = null;
          setWaitingQueue([]);
          resetRoundState();
          toast.success("All matchups completed! Loading results...");
          fetchFinalResults();
        } else if (applyServerMatchup(preserveWinnerSlot(data.next_matchup, selectedWinner, winnerIndex))) {
          console.log(
            "Next match from server:",
            data.next_matchup.participants.map(p => p.contributor_name)
          );
        } else {
          setCurrentMatch([null, null]);
          setCurrentMatchupId(null);
          matchRef.current = [null, null];
          matchupIdRef.current = null;
          resetRoundState();
          toast.info(data.next_message || "No next matchup is available yet.");
        }
        voteInFlightRef.current = false;
        isTransitioning.current = false;
      }, 800);
    } catch (error) {
      console.error("Error processing round:", error);
      toast.error(error.message || "Failed to record match result");
      setCurrentMatch([null, null]);
      setCurrentMatchupId(null);
      matchRef.current = [null, null];
      matchupIdRef.current = null;
      resetRoundState();
      voteInFlightRef.current = false;
      isTransitioning.current = false;
      fetchParticipants();
    }
  };
  
  // Check if contest has started before allowing access
  useEffect(() => {
    const checkContestStartTime = async () => {
      if (!contestId || !profile?.accessToken) {
        setCheckingStartTime(false);
        return;
      }

      try {
        const response = await authenticatedFetch(`${API_BASE}accounts/contests/${contestId}/`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response) return;

        if (response.ok) {
          const contestData = await response.json();
          setContestStartTime(contestData.start_time);
          setContestEndTime(contestData.end_time);
          
          // Check if contest has started
          const now = new Date();
          const startTime = contestData.start_time ? new Date(contestData.start_time) : null;
          const started = startTime && now >= startTime;
          
          // Check if user should be redirected to waiting room
          const isUser = profile?.role === "user" || profile?.role === "Judge";
          const needsWaitingRoom = !started && isUser && startTime;
          
          if (needsWaitingRoom) {
            // Redirect to waiting room if contest hasn't started
            toast.info("Contest hasn't started yet. Redirecting to waiting room...");
            navigate(`/waiting-room/${contestId}`);
            return;
          }
          
          setHasStarted(started);
          setCheckingStartTime(false);
          
          // Removed redundant fetchParticipants call
        } else {
          setCheckingStartTime(false);
          toast.error("Failed to load contest");
        }
      } catch (error) {
        console.error("Error checking contest start time:", error);
        setCheckingStartTime(false);
        toast.error("An error occurred");
      }
    };

    checkContestStartTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId, profile, navigate]);

  // Periodically check if contest has started
  useEffect(() => {
    if (!contestStartTime || hasStarted || checkingStartTime) return;

    const checkStartTime = () => {
      const now = new Date();
      const startTime = new Date(contestStartTime);
      const started = now >= startTime;
      
      if (started && !hasStarted) {
        setHasStarted(true);
        toast.success("Contest has started! Loading participants...");
      }
    };

    // Check immediately
    checkStartTime();

    // Check every second
    const interval = setInterval(checkStartTime, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestStartTime, hasStarted, checkingStartTime]);

  // Unified effect to fetch participants
  useEffect(() => {
    const isUser = profile?.role === "user" || profile?.role === "Judge";
    const shouldFetch = 
      contestId && 
      profile?.accessToken && 
      !checkingStartTime && 
      !matchupsCompleted &&
      (hasStarted || !isUser) && 
      currentMatch[0] === null;
    
    if (shouldFetch) {
      console.log("Fetching participants (unified effect)...");
      fetchParticipants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId, profile, hasStarted, checkingStartTime, currentMatch[0], matchupsCompleted]);

  // Start round when match is ready (only on initial load)
  useEffect(() => {
    if (currentMatch[0] && currentMatch[1] && !roundActive && !showResults && !winner) {
      startNewRound();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatch]);
  
  if (role === "Content Creator") return null;
  
  if (checkingStartTime) {
    return (
      <div className="flex min-h-screen bg-black">
        <Sidebar />
        <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full flex items-center justify-center">
          <p className="text-white text-xl">Checking contest status...</p>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-black">
        <Sidebar />
        <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full flex items-center justify-center">
          <p className="text-white text-xl">Loading contestants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Voting Panel" />

        {matchupProgress?.total_possible_matchups > 0 && (
          <div className="text-center text-xs md:text-sm text-gray-400 mt-2">
            Matchups completed: {matchupProgress.completed_matchups}/{matchupProgress.total_possible_matchups}
          </div>
        )}

        {/* Waiting List - Hidden but functionality preserved */}
        {false && waitingQueue.length > 0 && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-[#ceb46a]/30 rounded-lg p-5 mb-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#ceb46a] text-xl font-bold flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
                Waiting Queue
              </h3>
              <span className="bg-[#ceb46a] text-black px-3 py-1 rounded-full text-sm font-bold">
                {waitingQueue.length} Waiting
              </span>
            </div>
            {/* Horizontal scrollable row */}
            <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
              {waitingQueue.map((participant, index) => (
                <div 
                  key={participant.id}
                  className="flex items-center gap-2 bg-black/40 hover:bg-black/60 border border-[#ceb46a]/20 rounded-lg p-2 transition-all flex-shrink-0 w-[160px]"
                >
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#ceb46a] bg-gray-800">
                      {participant.image_url ? (
                        <img 
                          src={participant.image_url} 
                          alt={participant.contributor_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#ceb46a] text-lg font-bold">
                          {participant.contributor_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="absolute -top-1 -right-1 bg-[#ceb46a] text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{participant.contributor_name}</p>
                    <p className="text-[#ceb46a]/70 text-xs">Up next</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Voting Cards - ONLY 2 at a time */}
        {currentMatch[0] && currentMatch[1] ? (
          <>
            <div className="p-2 md:p-6 w-full flex flex-col justify-center sm:flex-row gap-6 items-stretch">
              {currentMatch.map((participant, index) => (
                <VotingCard 
                  key={`${currentMatchupId || "pending"}-${participant?.id || "empty"}-${index}`}
                  participant={participant}
                  onVote={() => handleVote(index === 0 ? 'left' : 'right')}
                  disabled={!roundActive || role !== "user" || hasVoted}
                  isWinner={winner?.id === participant?.id}
                />
              ))}
            </div>
            
            {/* Vote Confirmation Message */}
            {hasVoted && !showResults && (
              <div className="text-center mt-4">
                <div className="inline-block bg-green-600/20 border border-green-500 text-green-400 px-6 py-3 rounded-lg">
                  <p className="text-lg font-semibold">✅ Your vote has been recorded!</p>
                  <p className="text-sm mt-1">Determining winner...</p>
                </div>
              </div>
            )}
            
            {showResults && winner && (
              <div className="text-center mt-6">
                <div className="inline-block bg-gradient-to-r from-green-600 to-green-500 text-white px-8 py-4 rounded-lg shadow-lg">
                  <p className="text-2xl font-bold flex items-center gap-3 justify-center">
                    <span>🏆</span>
                    <span>Round Winner: {winner.contributor_name}</span>
                    <span>🏆</span>
                  </p>
                </div>
              </div>
            )}
            
            {role !== "user" && (
              <div className="text-center mt-4">
                <p className="text-gray-400 text-sm">📺 You are viewing as {role}. Only users can vote.</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-white py-10">
            {matchupsCompleted ? (
              <>
                <p className="text-xl">All matchups completed.</p>
                <p className="text-sm text-gray-400 mt-2">Results will be available after the contest ends.</p>
              </>
            ) : (
              <>
                <p className="text-xl">Waiting for contestants...</p>
                <p className="text-sm text-gray-400 mt-2">Need at least 2 participants to start voting</p>
              </>
            )}
          </div>
        )}

        {/* Final Results Modal */}
        {showFinalResults && finalResults && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50">
            <div className="bg-[#1C1C1E] text-white rounded-2xl shadow-lg w-11/12 max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-3xl font-bold text-[#ceb46a] mb-2 text-center">
                🏆 Contest Results 🏆
              </h2>
              <h3 className="text-xl text-gray-300 mb-6 text-center">
                {finalResults.contest_title}
              </h3>

              <div className="overflow-y-auto max-h-[60vh]">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 bg-[#1C1C1E]">
                    <tr className="bg-[#ceb46a] text-black">
                      <th className="py-3 px-4 text-left">Place</th>
                      <th className="py-3 px-4 text-left">Screen Name</th>
                      <th className="py-3 px-4 text-left">Votes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalResults.results.map((result, idx) => (
                      <tr
                        key={result.participant_id}
                        className={
                          idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                        }
                      >
                        <td className="py-4 px-4 font-bold text-lg">
                          {result.place}
                        </td>
                        <td className="py-4 px-4 font-semibold text-lg">
                          {result.screen_name}
                        </td>
                        <td className="py-4 px-4 text-[#ceb46a] font-bold text-lg">
                          {result.total_votes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-center mt-6 gap-4">
                <button
                  onClick={() => setShowFinalResults(false)}
                  className="px-6 py-3 rounded-lg font-semibold
                             bg-[#ceb46a] text-black
                             hover:bg-[#e3c97a] transition"
                >
                  Close
                </button>
                <button
                  onClick={() => navigate('/all-contests-history')}
                  className="px-6 py-3 rounded-lg font-semibold
                             bg-gray-700 text-white
                             hover:bg-gray-600 transition"
                >
                  View All Contests
                </button>
              </div>
            </div>
        </div>
        )}
      </div>
    </div>
  );
}
